import { Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import * as fromAccounts from './accounts/accounts.reducer';
import * as fromAccountSections from './account-sections/account-sections.reducer';

import * as fromLicenses from './licenses/licenses.reducer';
import { MigrationState } from '@shared';
import { License } from '../models/License';
import { IAccount, AccountState, Feature } from '../models';

const getAccountsState = createFeatureSelector<fromAccounts.State>(
  fromAccounts.ACCOUNTS_FEATURE_KEY,
);

export const getAccountCategories = createSelector(
  getAccountsState,
  (state) => state.accountCategories,
);

export const getAllAccounts = createSelector(
  getAccountsState,
  fromAccounts.getAllAccounts,
);

export const getAllEntities = createSelector(
  getAccountsState,
  fromAccounts.getAllEntities,
);

export const getAccountSortOn = createSelector(
  getAccountsState,
  fromAccounts.getSortAccountOnAsc,
);

export const getIsLoadingAccounts = createSelector(
  getAccountsState,
  fromAccounts.getIsLoadingAccounts,
);

export const getCreateAccountState = createSelector(
  getAccountsState,
  fromAccounts.getCreateAccountState,
);

export const getAccountStatus = createSelector(
  getAccountsState,
  fromAccounts.getAccountStatus,
);

export const getDeleteAccountState = createSelector(
  getAccountsState,
  fromAccounts.getDeleteAccountState,
);

export const getIsBootstrapComplete = createSelector(
  getAccountsState,
  fromAccounts.getIsBootstrapComplete,
);

export const getIsBootstrapFailed = createSelector(
  getAccountsState,
  fromAccounts.getIsBootstrapFailed,
);

export const getAccountMigrations = createSelector(
  getAccountsState,
  fromAccounts.getAccountMigrations,
);

export const getMappedProIds = createSelector(
  getAccountsState,
  fromAccounts.getAccountMigrations,
  ({ migrations }) => {
    const proAccountIds = migrations
      .filter((migration) => migration.status !== MigrationState.Paused)
      .map((m) => m.proAccountId);
    return proAccountIds;
  },
);

export const getIsLoadingMigrations = createSelector(
  getAccountsState,
  fromAccounts.getIsLoadingMigrations,
);

export const getAccountPackageState = createSelector(
  getAccountsState,
  fromAccounts.getAccountPackageState,
);

export const getAccountsCategoryFilter = createSelector(
  getAccountsState,
  ({ searchCategories }) => searchCategories,
);

export const getProAccounts = createSelector(
  getAccountsState,
  fromAccounts.getProAccounts,
);

export const getProAccountsIsLoading = createSelector(
  getAccountsState,
  fromAccounts.getProAccountsIsLoading,
);

export const getCrmKeyspaceIsCreating = createSelector(
  getAccountsState,
  fromAccounts.getCrmKeyspaceIsLoading,
);

export const getCrmKeyspaceTimeoutFailed = createSelector(
  getAccountsState,
  fromAccounts.getCrmKeyspaceTimeoutFailed,
);

export const getRebootstrapLicensesIsLoading = createSelector(
  getAccountsState,
  fromAccounts.getRebootstrapLicensesIsLoading,
);

export const getRebootstrapLincesesTimeoutFailed = createSelector(
  getAccountsState,
  fromAccounts.getRebootstrapLicensesTimeoutFailed,
);

export const getFilteredAccounts = createSelector(
  getAllAccounts,
  getAccountsCategoryFilter,
  getAccountSortOn,
  (accounts, searchCategories, sortOnAsc) => {
    const sortedAccounts = [...accounts].sort((a, b) => {
      return sortOnAsc
        ? new Date(a.created).getTime() - new Date(b.created).getTime()
        : new Date(b.created).getTime() - new Date(a.created).getTime();
    });
    return searchCategories.length
      ? sortedAccounts.filter((a) => searchCategories.includes(a.category))
      : sortedAccounts;
  },
);

const getLicensesState = createFeatureSelector<fromLicenses.LicensesState>(
  fromLicenses.LICENSES_FEATURE_KEY,
);

export const getAllowPolling = createSelector(
  getLicensesState,
  fromLicenses.getAllowPolling,
);

export const getAllLicences = createSelector(
  getLicensesState,
  fromLicenses.getAllLicences,
);

export const getAllLicencesIsLoaded = createSelector(
  getLicensesState,
  fromLicenses.getAllLicencesIsLoaded,
);

export const getLicenseEntities = createSelector(
  getLicensesState,
  fromLicenses.getAllEntities,
);

export const getLicensePackages = createSelector(
  getLicensesState,
  fromLicenses.getLicensePackages,
);

export const getIsLoadingPackages = createSelector(
  getLicensesState,
  fromLicenses.getIsLoadingPackages,
);

export const getPackagesIsLoaded = createSelector(
  getLicensesState,
  fromLicenses.getPackagesIsLoaded,
);

export const getLicensePackagesError = createSelector(
  getLicensesState,
  fromLicenses.getLicensePackagesError,
);

export const getHasFeatureLicense = (feature: Feature) =>
  createSelector(
    getLicenseEntities,
    (licenseEntities) => !!licenseEntities[feature]?.enabled,
  );

export const getAccountSectionsTotal =
  fromAccountSections.getAccountSectionsTotal;

export const getDisableAccountPackage = createSelector(
  getAllLicences,
  fromAccountSections.getAccountSectionsTotal,
  (allLisences: License[], sectionsTotal: number) =>
    !!(
      allLisences.some(
        (l) =>
          l.state === AccountState.Failed || l.state === AccountState.Pending,
      ) || !!(sectionsTotal >= 100)
    ),
);

export const getLicensesBootstrapState = createSelector(
  getAllLicences,
  (allLisences: License[]) => {
    const pendingOrFailed = allLisences.find((l) => {
      return (
        l.state === AccountState.Failed || l.state === AccountState.Pending
      );
    });
    return pendingOrFailed ? pendingOrFailed.state : AccountState.Success;
  },
);

export const getAccountById = (accountId: string) =>
  createSelector(
    getAllEntities,
    (entities: Dictionary<IAccount>) => entities[accountId],
  );
